import PetsIcon from "@mui/icons-material/Pets";
import {
  Avatar,
  Button,
  CardActions,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Typography,
} from "@mui/material";
import { useContext } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import SelectableCard from "../../../../../components/display/SelectableCard";
import SimpleAlert from "../../../../../components/display/SimpleAlert";
import UserHint from "../../../../../components/display/UserHint";
import DisplayChecker from "../../../../../components/wrappers/DisplayChecker";
import PageContentWrapper from "../../../../../components/wrappers/PageContentWrapper";
import { GlobalDataContext } from "../../../../../contexts/GlobalData";
import {
  SPECIES_ADD_TO_CLINIC,
  SPECIES_CLINIC_DTO,
} from "../../../../../enums/ApiPaths";
import { EDIT_SPECIES_LIST } from "../../../../../enums/PermissionTitles";
import { SPECIES_CLINIC } from "../../../../../enums/QueryKeys";
import { useFetch } from "../../../../../hooks/Fetching";
import usePrimaryColor from "../../../../../hooks/usePrimaryColor";
import CardBody from "../CardBody";
import useClinicSpeciesPut from "./hook/useClinicSpeciesPut";
import { useRedirectIfNotOwnClinic } from "../../../../../hooks/useRedirectIfNotOwnClinic";

export default function ClinicSpeciesPage() {
  const { id: clinicId } = useParams();
  useRedirectIfNotOwnClinic(clinicId);
  const { t } = useTranslation();
  const methods = useForm(); // Initialize react-hook-form
  const fetch = useFetch(SPECIES_CLINIC_DTO, clinicId, SPECIES_CLINIC);
  const color = usePrimaryColor();
  const { isPermissionAllowed } = useContext(GlobalDataContext);

  const submittion = useClinicSpeciesPut(
    SPECIES_ADD_TO_CLINIC + clinicId,
    SPECIES_CLINIC
  );
  const allSpeciesAreAllowed = fetch?.data?.allowsAllSpecies;

  // List of species associated with the clinic and all available species
  const clinicsSpecies = fetch?.data?.clinics || [];
  const allSpecies = fetch?.data?.species || [];
  const clinicSpeciesSet = new Set(clinicsSpecies.map((species) => species.id));

  if (fetch.isLoading) {
    return (
      <PageContentWrapper>
        <CircularProgress />
      </PageContentWrapper>
    );
  }

  if (allSpecies?.length === 0) {
    return (
      <SimpleAlert message={"NoSpeciesCurrentlyAvailable"} severity={"info"} />
    );
  }

  function submitSpecies(data) {
    const selectedSpeciesIds = Object.keys(data).filter(
      (key) => data[key] === true && key != "allowAllSpecies"
    );
    const submitData = {
      speciesIds: selectedSpeciesIds,
      allSpeciesAllowed: data["allowAllSpecies"],
    };
    submittion.submitCallback(submitData);
  }

  const editList = isPermissionAllowed(EDIT_SPECIES_LIST);

  return (
    <PageContentWrapper>
      <form onSubmit={methods.handleSubmit(submitSpecies)}>
        <Grid container justifyContent={"space-between"} alignItems={"center"}>
          <Grid item xs={12} md={10}>
            <Typography variant="h4" fontWeight={"bold"}>
              {t("AllAvailableSpecies")}
            </Typography>
          </Grid>
          {editList ? (
            <Grid item xs={12} md={2}>
              <Button
                type="submit"
                variant="contained"
                color="info"
                sx={{ mb: 2 }}
              >
                {t("Save")}
              </Button>
            </Grid>
          ) : null}
        </Grid>

        <Grid container justifyContent={"space-between"} alignItems={"center"}>
          <Grid item xs={12} md={10}>
            <UserHint hint={"AnimalSpeciesListUserHint"} />
          </Grid>
          {editList ? (
            <Grid item xs={12} md={2}>
              <Controller
                name="allowAllSpecies"
                control={methods.control}
                defaultValue={allSpeciesAreAllowed}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label={t("AllowAllSpecies")}
                  />
                )}
              />
            </Grid>
          ) : null}
        </Grid>

        <DisplayChecker
          error={fetch?.error}
          loading={fetch?.isLoading}
          data={fetch?.data}
          fullScreen={false}
          skeleton={false}
        >
          <Grid container>
            {allSpecies.map((species) => (
              <Grid
                item
                key={species?.id}
                width={{ xs: "100%", md: "auto" }}
                justifyContent={"center"}
                display={"flex"}
              >
                <Controller
                  name={`${species.id}`}
                  control={methods.control}
                  defaultValue={clinicSpeciesSet.has(species.id)}
                  render={({ field }) => (
                    <SelectableCard
                      small
                      checked={field.value}
                      onClick={() => field.onChange(!field.value)}
                    >
                      <Avatar
                        sx={{
                          bgcolor: color,
                          width: 30,
                          height: 30,
                          marginRight: 2,
                        }}
                      >
                        {species?.icon ? (
                          <i className={`fas ${species?.icon}`} />
                        ) : (
                          <PetsIcon />
                        )}
                      </Avatar>
                      <CardContent sx={{ flex: 1 }}>
                        <CardBody species={species} />
                      </CardContent>
                      {editList ? (
                        <CardActions>
                          <Checkbox
                            {...field}
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        </CardActions>
                      ) : null}
                    </SelectableCard>
                  )}
                />
              </Grid>
            ))}
          </Grid>
        </DisplayChecker>
      </form>
    </PageContentWrapper>
  );
}
